import React from 'react'
import { FamilyDetailsData } from '../../redux-store/types/investorProfile'
import { Avatar, Box, Skeleton, Theme, Typography, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { formatCurrency } from '../../utils/utilityFunctions'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux-store/reducers'


const FamilyCardSkeleton = () => {
    return (
        <Box
            sx={(theme) => ({
                backgroundColor: theme.palette.common.white,
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                padding: { xs: "1rem 0.5rem", md: "2rem 1.5rem" },
                borderRadius: "1.5rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
            })}
        >
            {/* LEFT SECTION */}
            <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                {/* Avatar Skeleton */}
                <Skeleton variant="circular" width={56} height={56} />

                {/* Name + Label */}
                <Box>
                    <Skeleton width={120} height={24} />
                    <Skeleton width={60} height={18} />
                </Box>
            </Box>

            {/* RIGHT SECTION */}
            <Box sx={{ textAlign: "right" }}>
                {/* Market Value Label */}
                <Skeleton width={100} height={20} sx={{ ml: "auto" }} />

                {/* Date */}
                <Skeleton width={120} height={18} sx={{ ml: "auto", mb: 1 }} />

                {/* Amount */}
                <Skeleton width={140} height={35} sx={{ ml: "auto" }} />

                {/* Bottom Stats */}
                <Box
                    sx={{
                        display: "flex",
                        gap: "2rem",
                        justifyContent: "flex-end",
                        mt: 3,
                    }}
                >
                    {[1, 2, 3].map((_, i) => (
                        <Box key={i}>
                            <Skeleton width={80} height={18} />
                            <Skeleton width={60} height={20} />
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};


export default function MainFamilyInfo({
    familyData,
    familyDataLoading
}: {
    familyData: FamilyDetailsData,
    familyDataLoading: boolean
}): JSX.Element {

    const { businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
    const { locale, currencyCode } = useSelector((state: RootStateType) => state.currencyInfo);
    const theme = useTheme();

    const investmentValues = [
        {
            label: 'Total Investment',
            value: `${formatCurrency({ value: familyData?.totalInvestment ?? 0, locale, currencyCode })}`,
            color: theme.palette.text.primary
        },
        {
            label: 'Returns',
            value: `${familyData?.returns > 0 ? '+' : ''} ${formatCurrency({ value: familyData?.returns ?? 0, locale, currencyCode })}`,
            color: familyData?.returns >= 0 ? theme.palette.success[500] : theme.palette.error[500]
        },
        {
            label: 'IRR',
            value: `${familyData?.irr?.toFixed(1)}%`,
            color: familyData?.irr >= 0 ? theme.palette.success[500] : theme.palette.error[500]
        }
    ]

    if (familyDataLoading) return <FamilyCardSkeleton />

    const familyName = (familyData?.familyName?.trim() ?? "")?.split(" ");
    const firstInitial = (familyName?.[0] ?? "")?.charAt(0)?.toUpperCase() ?? "";
    const secondInitial = (familyName?.[1] ?? "")?.charAt(0)?.toUpperCase() ?? "";
    return (
        <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                padding: { xs: '1rem 0.5rem', md: '2rem 1.5rem' },
                borderRadius: "1.5rem",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",

            })}>
            {/* Avatar */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: '0.5rem' }}>
                <Avatar
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.primary[100],
                        padding: '1.5rem'
                    })}
                >
                    <Typography
                        variant="h1"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500]
                        })}
                    >
                        {firstInitial + secondInitial}
                    </Typography>
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "32.5px",
                            color: theme.palette.text.primary
                        })}
                    >
                        {familyData?.familyName}
                    </Typography>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.secondary
                        })}
                    >
                        Family
                    </Typography>
                </Box>
            </Box>
            {/* Details */}
            <Box>
                <Box>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "24px",
                            letterSpacing: "0.02px",
                            textAlign: "right",
                            color: theme.palette.text.primary
                        })}
                    >
                        Market Value
                    </Typography>
                    <Typography
                        variant="subtitle_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "21px",
                            letterSpacing: "0.02px",
                            textAlign: "right",
                            color: theme.palette.text.primary
                        })}
                    >
                        {/* as on 30 Jan, 2024 */}
                        as on {dayjs(businessDate, 'DD/MM/YYYY').format('DD MMM, YYYY')}
                    </Typography>
                    <Typography
                        variant="h1"
                        sx={(theme: Theme) => ({
                            lineHeight: "40px",
                            color: theme.palette.primary[500],
                            textAlign: "right",
                            whiteSpace: "nowrap"
                        })}
                    >
                        {formatCurrency({ value: familyData?.marketValue, locale, currencyCode })}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2rem', mt: 3 }}>
                    {investmentValues.map((value, index) => (
                        <Box key={index}>
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "24px",
                                    letterSpacing: "0.02px",
                                    textAlign: "right",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {value.label}
                            </Typography>
                            <Typography
                                variant="body_600"
                                sx={(theme: Theme) => ({
                                    lineHeight: "21px",
                                    letterSpacing: "0.02px",
                                    textAlign: "right",
                                    color: value.color
                                })}
                            >
                                {value.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}
